import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import apiConfigurations from 'api/configurations';
import IRouteParams from 'interfaces/IRouteParams';
import SidebarDeleteIcon from 'components/layout/buttons/SidebarDeleteIcon';
import SidebarDistanceMeasureIcon from 'components/layout/buttons/SideBarDistanceMeasureIcon';
import translations from 'services/translations/translations';
import { addApproval } from 'store/messages/actions';

import SidebarInfoIcon from 'components/layout/buttons/SidebarInfoIcon';
import BuildingWallColorIcon from './BuildingWallColorIcon';
import ConnectionVisibilityIcon from './ConnectionVisibilityIcon';
import DimensionToggle from './DimensionToggle';
import SaveConfiguration from './SaveConfiguration';
import SpotlightIcon from './SpotlightIcon';
import SnapshotButton from './SnapshotButton';
import { allowedToDeleteOwnConfiguration } from '../services/permissions/allowedToDeleteOwnConfiguration';
import { allowedToWriteOwnOrganizationConfiguration } from '../services/permissions/allowedToWriteOwnOrganizationConfiguration';
import { THREE_D } from '../types/dimensions';
import 'style/configurator/LeftBar.css';
import ToggleEdgesIcon from './ToggleEdgesIcon';
import {
  informationPickedActive,
  SetFilterObjects,
} from '../../../../public/cadcraft/ConfigurationGeometry/MasterAssembly';
import { Functions } from '../../../../public/cadcraft/CadcraftForgeUtils/CadcraftForgeUtils';

const LeftBar = ({
  configName,
  dimensionStatus,
  setDimensionStatus,
  settings,
  openInfoBar,
}: {
  configName: string;
  dimensionStatus: string;
  setDimensionStatus: (status: string) => void;
  settings: any;
  openInfoBar: any;
}): JSX.Element => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation([translations.common]);
  const configuration = useSelector((state: any) => state.configuration);
  const currentUser = useSelector((state: any) => state.users.current);

  const { configurationId } = useParams<IRouteParams>();

  const removeConfiguration = async () => {
    await apiConfigurations.delete(configurationId);
    history.push(`/projects/${configuration.project.id}`);
  };

  const onRemoveButtonClick = () => {
    dispatch(
      addApproval({
        callback: removeConfiguration,
        message: t(
          `${translations.common}:Are you sure that you want to remove the configuration?`
        ),
      })
    );
  };

  const onDistanceMeasureClick = () => {
    window.MeasureBetweenPoints();
    document.addEventListener('mouseup', showMeasuredDistance);
  };

  const showMeasuredDistance = (e: any) => {
    if (window.GetPointDistances() !== undefined) {
      alert(
        t(
          `${translations.common}:Distance measured ${
            window.GetPointDistances().alignDistance
          }`
        )
      );
    }
    document.removeEventListener('mouseup', showMeasuredDistance);
  };

  const onInfoButtonClick = () => {
    window.GetInfoFromPart((pickerObject: any) => {
      openInfoBar(pickerObject);
      window.GetInfoFromPart();
    });
  };

  const showDelete =
    configurationId !== undefined &&
    currentUser !== null &&
    (currentUser.allowedToDeleteConfigurations ||
      allowedToDeleteOwnConfiguration(currentUser, configuration));

  const showEdit =
    currentUser.allowedToWriteConfigurations ||
    allowedToWriteOwnOrganizationConfiguration(
      currentUser,
      configuration?.project
    );

  return (
    <div className="configurator-left-bar">
      <DimensionToggle
        dimensionStatus={dimensionStatus}
        setDimensionStatus={setDimensionStatus}
      />
      {showEdit ? <SaveConfiguration configName={configName} /> : null}
      <SnapshotButton settings={settings} />
      <SpotlightIcon settings={settings} />
      {dimensionStatus === THREE_D ? (
        <SidebarInfoIcon
          className="left-bar-icon"
          onClick={onInfoButtonClick}
        />
      ) : null}
      <BuildingWallColorIcon />
      <SidebarDistanceMeasureIcon
        className="left-bar-icon"
        onClick={onDistanceMeasureClick}
      />
      <ToggleEdgesIcon />
      <ConnectionVisibilityIcon />
      {showDelete ? (
        <SidebarDeleteIcon
          className="left-bar-icon"
          onClick={onRemoveButtonClick}
        />
      ) : null}
    </div>
  );
};

export default LeftBar;
